import { all, takeLatest, put, call, select, cancelled } from 'redux-saga/effects';
import FileSaver from 'file-saver';
import queryString from 'query-string';
import { AXIOS_CANCEL_CODE } from '../../config/axios';
import { getClaimsInsuredRisksFilterOptions } from '../filters/slice';
import { getFileNameFromApiResponseHeader } from '../../helpers/utility';
import { onGetRisk } from '../risk/api';
import { selectClaimsActiveFilters } from '../filters/selectors';
import { onGetClaims, onGetMapRisks, onExportClaims } from './api';
import {
  getClaims,
  getClaimsSucceeded,
  getClaimsFailed,
  exportClaims,
  exportClaimsSucceeded,
  exportClaimsFailed,
  getMapRisks,
  getMapRisksSucceeded,
  getMapRisksFailed,
  getMapSelectedRisk,
  getMapSelectedRiskSucceeded,
  getMapSelectedRiskFailed,
  getMapSelectedRiskClaims,
  getMapSelectedRiskClaimsSucceeded,
  getMapSelectedRiskClaimsFailed,
  getPolicyDetailsClaims,
  getPolicyDetailsClaimsSucceeded,
  getPolicyDetailsClaimsFailed,
  getRiskDetailsClaims,
  getRiskDetailsClaimsSucceeded,
  getRiskDetailsClaimsFailed,
  getClaimsByDateAndLocation,
  getClaimsByDateAndLocationSucceeded,
  getClaimsByDateAndLocationFailed,
  clearClaimsByDateAndLocation
} from './slice';

export function* getClaimsWorker({ payload }) {
  const { params } = payload;
  const controller = new AbortController();

  try {
    const response = yield call(onGetClaims, params, controller.signal);
    yield put(
      getClaimsSucceeded({
        claims: response.data,
        meta: {
          perPage: response.headers.perpage,
          total: response.headers.total
        }
      })
    );
  } catch (error) {
    if (error?.code !== AXIOS_CANCEL_CODE) {
      yield put(getClaimsFailed());
    }
  } finally {
    if (yield cancelled()) {
      controller.abort('Search cancelled by takeLatest');
    }
  }
}

export function* getMapRisksWorker({ payload }) {
  const { params } = payload;
  try {
    const response = yield call(onGetMapRisks, params);
    yield put(getMapRisksSucceeded(response.data));
  } catch {
    yield put(getMapRisksFailed());
  }
}

export function* getMapSelectedRiskWorker({ payload }) {
  const { riskId } = payload;
  try {
    const response = yield call(onGetRisk, riskId);
    yield put(getMapSelectedRiskSucceeded(response.data));
  } catch {
    yield put(getMapSelectedRiskFailed());
  }
}

export function* getMapSelectedRiskClaimsWorker({ payload }) {
  const { riskId, params } = payload;
  try {
    const response = yield call(onGetClaims, { risk_id: riskId, ...params });
    yield put(getMapSelectedRiskClaimsSucceeded(response.data));
  } catch {
    yield put(getMapSelectedRiskClaimsFailed());
  }
}

export function* exportClaimsWorker({ payload }) {
  const { params } = payload;

  try {
    const claimsWithoutPagination = yield call(onGetClaims, { ...params, paging: false });
    const claimsIds = claimsWithoutPagination.data.map((claim) => claim.id);
    const response = yield call(onExportClaims, claimsIds);

    const filename = getFileNameFromApiResponseHeader(response.headers, '.xlsx');

    // only IE does support this method
    if (window.navigator.msSaveOrOpenBlob) {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const blob = new File([response.data], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet:base64'
      });
      FileSaver.saveAs(blob);
    }

    yield put(exportClaimsSucceeded());
  } catch {
    yield put(exportClaimsFailed());
  }
}

export function* getPolicyDetailsClaimsWorker({ payload }) {
  const { policyId, search } = payload;
  try {
    const params = { policy_id: policyId, search, paging: false };
    const response = yield call(onGetClaims, params);
    yield put(getPolicyDetailsClaimsSucceeded(response.data));
  } catch {
    yield put(getPolicyDetailsClaimsFailed());
  }
}

export function* getRiskDetailsClaimsWorker({ payload }) {
  const { riskId, search } = payload;
  try {
    const params = { risk_id: riskId, search, paging: false };
    const response = yield call(onGetClaims, params);
    yield put(getRiskDetailsClaimsSucceeded(response.data));
  } catch {
    yield put(getRiskDetailsClaimsFailed());
  }
}

export function* reloadClaimsWorker() {
  try {
    const claimsListActiveFilters = yield select(selectClaimsActiveFilters);
    const params = queryString.parse(claimsListActiveFilters);
    // if there is no sort param, we need to set default sort by updated_at_claim_and_settlements_and_messages
    const newParams = {
      sort: '-updated_at_claim_and_settlements_and_messages',
      ...params
    };

    yield all([put(getClaims({ params: newParams })), put(getClaimsInsuredRisksFilterOptions())]);
  } catch {
    yield put(getClaimsFailed());
  }
}

export function* getClaimsByDateAndLocationWorker({ payload }) {
  const { params } = payload;
  try {
    const hasValidDate = !!params?.formattedDate;
    const hasValidRisk = !!params?.risk_id || !!params?.risk_text;

    if (!hasValidDate || !hasValidRisk) {
      // If either the date or the risk parameter is missing, clear the claims and exit
      yield put(clearClaimsByDateAndLocation());
      return;
    }

    const apiParams = {
      date_of_damage_from: params?.formattedDate,
      date_of_damage_until: params?.formattedDate
    };

    if (params?.risk_id) {
      apiParams.risk_id = params.risk_id;
    }

    if (params?.risk_text) {
      apiParams.search = params.risk_text;
    }

    const response = yield call(onGetClaims, apiParams);

    let filteredData = response.data;

    // filter results by risk_text if it is provided
    if (params.risk_text) {
      filteredData = response.data.filter((item) => item.risk_text === params?.risk_text);
    }

    yield put(getClaimsByDateAndLocationSucceeded(filteredData));
  } catch {
    yield put(getClaimsByDateAndLocationFailed());
  }
}

export default function* claimsSaga() {
  yield all([
    takeLatest(getClaims.type, getClaimsWorker),
    takeLatest(exportClaims.type, exportClaimsWorker),
    takeLatest(getMapRisks.type, getMapRisksWorker),
    takeLatest(getMapSelectedRisk.type, getMapSelectedRiskWorker),
    takeLatest(getMapSelectedRiskClaims.type, getMapSelectedRiskClaimsWorker),
    takeLatest(getClaimsByDateAndLocation.type, getClaimsByDateAndLocationWorker),
    takeLatest(getPolicyDetailsClaims.type, getPolicyDetailsClaimsWorker),
    takeLatest(getRiskDetailsClaims.type, getRiskDetailsClaimsWorker)
  ]);
}
